/**
 * Modal-related @apply property sets from src/styles/propertySets.css, as sx objects.
 * Mirrors the source verbatim. Custom-media → literal sx keys:
 * --viewportSmall '@media (min-width:600px)', --viewportMedium '@media (min-width:900px)',
 * --viewportLarge '@media (min-width:1200px)', --viewportLargeWithPaddings '@media (min-width:1304px)',
 * --viewportXLarge '@media (min-width:1921px)'.
 *
 * Sets containing @apply are expanded inline: the @apply target's declarations are inlined at the
 * point of use, with subsequent own declarations overriding conflicting ones (last-wins, per CSS).
 * Media blocks from @apply and own media blocks are merged into a single key.
 *
 * @param {Record<string,string>} tokens - createTokens() result
 * @returns {Record<string, object>}
 */
export const createModalStyleMixins = (tokens) => ({
  marketplaceModalBaseStyles: {
    flexGrow: '1',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    padding: tokens.modalPadding,
    backgroundColor: tokens.matterColorLight,
    borderRadius: tokens.borderRadius,
    borderBottom: 'none',
    '@media (min-width:900px)': {
      flexBasis: '480px',
      flexGrow: '0',
      padding: tokens.modalPaddingMedium,
      marginTop: '12.5vh',
      marginBottom: '12.5vh',
      borderBottom: `8px solid ${tokens.marketplaceColor}`,
    },
  },

  marketplaceModalInMobileBaseStyles: {
    flexGrow: '1',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    padding: '0',
    backgroundColor: tokens.matterColorLight,
    borderRadius: tokens.borderRadius,
    '@media (min-width:900px)': {
      flexBasis: '480px',
      flexGrow: '0',
      height: 'auto',
      padding: '0',
      backgroundColor: 'transparent',
      borderRadius: '0',
    },
  },

  marketplaceModalRootStyles: {
    flexGrow: '1',
    display: 'flex',
    '@media (min-width:900px)': {
      justifyContent: 'center',
      alignItems: 'flex-start',
    },
  },

  marketplaceModalFormRootStyles: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1',
    justifyContent: 'space-between',
    height: '100%',
  },

  // NOTE: propertySets.css defines --marketplaceModalTitleStyles TWICE; postcss-apply is last-wins,
  // so the SECOND block (line ~736: fontWeightBold + media letter-spacing -0.9px) is the effective one.
  // Verified against the running app: every expanded modal-title rule resolves to var(--fontWeightBold).
  marketplaceModalTitleStyles: {
    fontWeight: tokens.fontWeightBold,
    fontSize: '30px',
    lineHeight: '36px',
    letterSpacing: '-0.5px',
    margin: '0',
    '@media (min-width:900px)': {
      fontWeight: tokens.fontWeightSemiBold,
      lineHeight: '40px',
      letterSpacing: '-0.9px',
      margin: '0',
    },
  },

  marketplaceModalParagraphStyles: {
    margin: '18px 0 0 0',
    '@media (min-width:900px)': {
      margin: '24px 0 0 0',
    },
  },

  // @apply --marketplaceH4FontStyles expanded inline; own marginTop: 24px overrides H4's 12px.
  // H4 media block is included; no own media additions.
  marketplaceModalErrorStyles: {
    fontWeight: tokens.fontWeightMedium,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0',
    marginTop: '24px',
    marginBottom: '12px',
    color: tokens.failColor,
    '@media (min-width:900px)': {
      fontWeight: tokens.fontWeightMedium,
      fontSize: '20px',
      marginTop: '17px',
      marginBottom: '15px',
    },
  },

  // @apply --marketplaceTinyFontStyles expanded inline; own margin: 0 overrides tiny's margin-top/bottom
  // shorthand-wins for both base and media block.
  marketplaceModalHelperText: {
    fontFamily: tokens.fontFamily,
    fontWeight: tokens.fontWeightRegular,
    fontSize: '13px',
    lineHeight: '18px',
    WebkitFontSmoothing: 'subpixel-antialiased',
    color: tokens.matterColorAnti,
    margin: '0',
    '@media (min-width:900px)': {
      lineHeight: '16px',
      margin: '0',
    },
  },

  // @apply --marketplaceTinyFontStyles expanded inline; own margin: 0 overrides tiny's margin-top/bottom
  // shorthand-wins for both base and media block.
  marketplaceModalHelperLink: {
    fontFamily: tokens.fontFamily,
    fontWeight: tokens.fontWeightRegular,
    fontSize: '13px',
    lineHeight: '18px',
    WebkitFontSmoothing: 'subpixel-antialiased',
    color: tokens.matterColor,
    margin: '0',
    '@media (min-width:900px)': {
      lineHeight: '16px',
      margin: '0',
    },
  },

  marketplaceModalBottomWrapper: {
    textAlign: 'center',
    marginTop: '60px',
    alignSelf: 'stretch',
    '@media (min-width:900px)': {
      marginTop: '96px',
    },
  },

  marketplaceModalBottomWrapperText: {
    marginTop: '-24px',
    paddingBottom: '8px',
    '@media (min-width:900px)': {
      paddingBottom: '0',
    },
  },

  marketplaceModalCloseStyles: {
    position: 'fixed',
    top: '0',
    right: '0',
    zIndex: `calc(${tokens.zIndexModal} + 1)`,
    display: 'flex',
    alignItems: 'flex-start',
    width: 'auto',
    padding: '24px',
    margin: '0',
    border: '0',
    '@media (min-width:900px)': {
      padding: '27px 30px',
      position: 'absolute',
    },
    backgroundColor: 'transparent',
    color: tokens.matterColor,
    cursor: 'pointer',
    '&:enabled:hover': {
      backgroundColor: 'transparent',
      color: tokens.matterColorDark,
      boxShadow: 'none',
      textDecoration: 'none',
    },
    '&:enabled:active': {
      backgroundColor: 'transparent',
      color: tokens.matterColorDark,
    },
    '&:disabled': {
      backgroundColor: 'transparent',
    },
  },

  // @apply --marketplaceH6FontStyles expanded inline.
  // Own margin: -2.5px 0 0 0 overrides H6's marginTop/marginBottom.
  // H6 media block merged with own media marginTop: -1px.
  marketplaceModalCloseText: {
    fontWeight: tokens.fontWeightBold,
    fontSize: '12px',
    lineHeight: '18px',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    margin: '-2.5px 0 0 0',
    '@media (min-width:900px)': {
      lineHeight: '16px',
      marginTop: '-1px',
      marginBottom: '6px',
    },
  },

  marketplaceModalCloseIcon: {
    display: 'inline-block',
    marginLeft: '8px',
    padding: '2px 0 4px',
    boxSizing: 'content-box',
  },

  marketplaceModalIconStyles: {
    height: '48px',
    marginBottom: '12px',
    '@media (min-width:900px)': {
      height: '64px',
      marginBottom: '23px',
    },
  },

  marketplaceModalPasswordMargins: {
    marginTop: '24px',
    '@media (min-width:900px)': {
      marginTop: '32px',
    },
  },
});
